import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Trip } from '../../../shared/trip.model';
import { TripDevicePath } from '../../../shared/trip-path.model';
import { NavigationService } from '../../../../shared/services/navigation.service';
import { ClipboardService } from '../../../../shared/services/clipboard.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../../shared/services/modal.service';
import { finalize } from 'rxjs/operators';
import { LoaderService } from '../../../../shared/services/loader.service';
import { TripService } from '../../../shared/trip.service';
import { Markers } from '../../../../shared/markers';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { LoadHandover } from '../../../../load-handover/shared/load-handover.model';
import * as moment from 'moment';
import { DistanceUtils } from '../../../../shared/distance-utils';
import { DistanceUnit } from '../../../../shared/distance-unit';
import { TripStatus } from 'app/realtime-tracking/shared/trip-status';
import { TripEventType } from '../../../shared/trip-event-type';
import { TripEvent } from '../../../shared/trip-event.model';
import { TripDelayType } from '../../../shared/trip-delay-type';
import { NavigationLinkService } from '../../../../shared/services/navigation-link.service';
import LatLngLiteral = google.maps.LatLngLiteral;
import { DateTimezoneService } from '../../../shared/date-timezone.service';
import { DateService } from '../../../../shared/date.service';
import { Moment } from 'moment/moment';

@Component({
  selector: 'app-load-tracking-trip-details',
  templateUrl: './load-tracking-trip-details.component.html',
  styleUrls: ['./load-tracking-trip-details.component.scss']
})
export class LoadTrackingTripDetailsComponent implements OnChanges, OnInit {

  @Input()
  singleTrip = false;

  @Input()
  trip: Trip;

  @Input()
  debug = false;

  @Input()
  showGoogleEta = false;

  @Input()
  distanceUnit = DistanceUnit.KILOMETERS

  @Output()
  tripChange = new EventEmitter<Trip>();

  @Output()
  onRefresh = new EventEmitter<void>();

  @Output()
  onGoToMarker = new EventEmitter<LatLngLiteral>();

  @Output()
  onUpdateLoadHandover = new EventEmitter<LoadHandover>();

  chepDepartureIconUrl         = Markers.DEPARTURE_LOAD.chep
  customerDepartureIconUrl     = Markers.DEPARTURE_LOAD.customer;
  passiveReleaseIconUrl        = Markers.RELEASE.blue;
  passivePickIconUrl           = Markers.PICK_UP.blue;
  chepArrivalIconUrl           = Markers.ARRIVAL_LOAD.chep
  customerArrivalIconUrl       = Markers.ARRIVAL_LOAD.customer;
  passiveTrailerIconUrl        = Markers.TRAILER.grey;
  passiveCheckInIconUrl        = Markers.CHECKIN;
  passiveCheckOutIconUrl        = Markers.CHECKOUT;

  modalNotificationTitle: string;
  modalNotificationMessage: string;

  TripEventType = TripEventType;
  TripStatus = TripStatus;
  tripStatusInfo: {isHandover: boolean, duration: string, formatNeeded: boolean};
  tripDelayStatus = TripDelayType.UNKNOWN;
  TripDelayStatus = TripDelayType;

  tripVehicles: string;
  tripStartDate: Moment;
  tripStartDuration: string;
  tripStartFormat = true;
  tripEndDate: Moment;
  tripEndDuration: string;
  tripEndFormat = true;

  isEta = false;
  etaTime: string;
  latestEta: Moment;
  etaDuration: string;
  delayDuration: string;
  etaFormat = false;
  etaDiff: string;
  firstEtaOrigin: string;
  latestEtaUpdatedAt: Moment;
  etaUpdatedAtDuration: string;
  etaUpdatedAtFormat = true;
  etaOverCap: boolean;

  lastPingDuration: string;
  lastPingFormat = false;
  lastPingDistance: string;

  events: Array<TripEvent> = [];
  eventsDate: Array<Moment> = [];
  eventsDuration: Array<string> = [];
  eventsFormat: Array<boolean> = [];

  dateFormat = 'D MMM LT (z)';

  constructor(
    private tripService: TripService,
    private loader: LoaderService,
    private translate: TranslateService,
    private navigation: NavigationService,
    public link: NavigationLinkService,
    private toastr: ToastrService,
    private clipboard: ClipboardService,
    private modal: ModalService,
    private errorHandler: ErrorHandlerService,
    private dateService: DateService,
    public timeZoneService: DateTimezoneService
  ) {}

  ngOnInit() {
    this.dateService.localeChanged.subscribe(() => {
      this.ngOnChanges(null)
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.trip) {
      this.initData();
    }
  }

  initData(): void {
    if (this.singleTrip) {
      this.timeZoneService.resetToDefaultTimezone();
    }
    this.tripVehicles = this.trip.getVehicles();
    this.tripDelayStatus = this.trip.getTripStatus();
    this.tripStartDate = this.trip.recordedAt;
    ({duration: this.tripStartDuration, formatNeeded: this.tripStartFormat} = this.dateService.durationFrom(this.trip.recordedAt));
    if (this.trip.status === TripStatus.COMPLETED || this.trip.status === TripStatus.EXPIRED) {
      const {duration, formatNeeded} = this.dateService.durationFrom(this.trip.recordedAt, this.trip.completedAt);
      this.tripStatusInfo = {
        isHandover: false,
        duration: duration,
        formatNeeded: formatNeeded
      };
      this.tripEndDate = this.trip.completedAt;
      ({duration: this.tripEndDuration, formatNeeded: this.tripEndFormat} = this.dateService.durationFrom(this.trip.completedAt));
      if (this.tripDelayStatus === TripDelayType.LATE) {
        this.delayDuration = this.trip.isNotTerminated()
                           ? this.dateService.durationTo(this.trip.loadPlanningEnd, moment(), true).duration
                           : this.dateService.durationTo(this.trip.completedAt, this.trip.loadPlanningEnd, true).duration;
      }
    } else if (this.trip.loadHandovers.length > 0 && !this.trip.loadHandovers[this.trip.loadHandovers.length - 1].takenAt) {
      const {duration, formatNeeded} = this.dateService.durationFrom(this.trip.loadHandovers[this.trip.loadHandovers.length - 1].releasedAt);
      this.tripStatusInfo = {
        isHandover: true,
        duration: duration,
        formatNeeded: formatNeeded
      };
    } else {
      let duration = null;
      let format = null;
      if (this.trip.latestPing) {
        duration = this.dateService.durationFrom(this.trip.latestPingCreatedAt).duration;
        format = this.dateService.durationFrom(this.trip.latestPingCreatedAt).formatNeeded;
      }
      this.tripStatusInfo = {
        isHandover: false,
        duration: duration,
        formatNeeded: format
      };
    }
    this.latestEta = this.trip.latestEta();
    if (this.latestEta) {
      this.isEta = true;
      this.etaTime = this.trip.latestEta().format('LT');
      if (this.trip.loadPlanningEnd) {
        ({duration: this.etaDiff} = this.dateService.durationFrom(this.latestEta, this.trip.loadPlanningEnd));
        this.firstEtaOrigin = this.trip.loadPlanningEnd.isBefore(this.latestEta) ? 'provided' : 'current';
      } else {
        this.etaDiff = null;
      }
      ({duration: this.etaDuration, formatNeeded: this.etaFormat} = this.dateService.durationFrom(this.trip.latestEta()));
      this.latestEtaUpdatedAt = this.trip.latestEtaUpdatedAt();
      ({duration: this.etaUpdatedAtDuration, formatNeeded: this.etaUpdatedAtFormat} = this.dateService.durationFrom(this.trip.latestEtaUpdatedAt()));
      this.etaOverCap = this.latestEta.isBefore(moment());
    }
    if (this.trip.latestPing) {
      ({duration: this.lastPingDuration, formatNeeded: this.lastPingFormat} = this.dateService.durationFrom(this.trip.latestPingCreatedAt));
      const distance = DistanceUtils.distanceBetweenCoords(
        this.trip.latestPing.latitude, this.trip.latestPing.longitude,
        this.trip.destPosition.lat, this.trip.destPosition.lng,
        this.distanceUnit
      );
      this.lastPingDistance = distance.toPrecision(Math.floor(Math.max(2, Math.log10(distance) + 1)))
    }
    this.events = this.trip.events.filter(event => event.type !== TripEventType.ETA);
    this.eventsDuration = [];
    this.eventsDate = [];
    this.eventsFormat = [];
    for (const event of this.events) {
      this.eventsDate.push(event.happenedAt);
      const {duration, formatNeeded} = this.dateService.durationFrom(event.happenedAt);
      this.eventsDuration.push(duration);
      this.eventsFormat.push(formatNeeded);
    }
  }

  checkAllPaths(devicePaths: TripDevicePath): void {
    devicePaths.paths.forEach(p => p.displayed = devicePaths.selectAllPaths);
    devicePaths.chainPathsBetweenOneAnother();
  }

  checkPath(devicePaths: TripDevicePath): void {
    devicePaths.chainPathsBetweenOneAnother();
  }

  copyCustomerLink(): void {
    const url    = this.navigation.realtimeTracking().trackATrip(this.trip.customerId).getUrl();
    const copied = this.clipboard.copy(url);

    if (copied) {
      this.toastr.success(this.translate.instant('realtime-tracking.load-tracking-map.trip-details.link.copy-success'));
    } else {
      this.toastr.error(this.translate.instant('realtime-tracking.load-tracking-map.trip-details.link.copy-error'));
    }
  }

  refreshTripDetails(): void {
    this.onRefresh.emit();
    setTimeout(() => this.initData(), 1000);
  }

  openConfirmCloseSelectedTrip(modalContent: any): void {
    this.modal.open({
      title: this.translate.instant('realtime-tracking.load-tracking-map.trip-details.close.modal.title'),
      content: modalContent,
      validateButton: {
        text: this.translate.instant('realtime-tracking.load-tracking-map.trip-details.close.modal.buttons.yes'),
        icon: 'fa fa-lock'
      }
    }).subscribe(() => this.closeSelectedTrip());
  }

  closeSelectedTrip(): void {
    this.loader.startLoad();

    this.tripService.close(this.trip)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.trip.status = TripStatus.COMPLETED;
        this.refreshTripDetails();
        this.toastr.success(this.translate.instant('realtime-tracking.load-tracking-map.trip-details.close.success'));
      }, e => this.errorHandler.handleHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.close.error', false));
  }

  copy(value: string): void {
    const copied = this.clipboard.copy(value);

    if (copied) {
      this.toastr.success(this.translate.instant('copy.success'));
    } else {
      this.toastr.error(this.translate.instant('copy.error'));
    }
  }

  openNotification(modalContent: any, trip: Trip): void {
    this.modalNotificationTitle   = this.translate.instant('realtime-tracking.load-tracking-map.trip-details.notifications.update-eta.default-title', { loadNumber: trip.loadNumber });
    this.modalNotificationMessage = this.translate.instant('realtime-tracking.load-tracking-map.trip-details.notifications.update-eta.default-message', { loadNumber: trip.loadNumber });

    this.modal.open({
      title: this.translate.instant('realtime-tracking.load-tracking-map.trip-details.notifications.custom.label'),
      content: modalContent,
      validateButton: {
        text: this.translate.instant('realtime-tracking.load-tracking-map.trip-details.notifications.custom.modal.button'),
        icon: 'fa fa-paper-plane',
        disabled: () => !this.modalNotificationTitle || !this.modalNotificationMessage
      }
    }).subscribe(() => this.sendNotification(trip, this.modalNotificationTitle, this.modalNotificationMessage));
  }

  sendNotification(trip: Trip, title: string, message: string): void {
    this.loader.startLoad();

    if (!message) {
      message = this.translate.instant(
        'realtime-tracking.load-tracking-map.trip-details.notifications.update-eta.default-message',
        { loadNumber: trip.loadNumber }
      );
    }
    if (!title) {
      title = this.translate.instant('realtime-tracking.load-tracking-map.trip-details.notifications.update-eta.default-title');
    }

    this.tripService.sendCustomNotification(trip, title, message)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.toastr.success(
          this.translate.instant('realtime-tracking.load-tracking-map.trip-details.notifications.custom.success')
        );
      }, e => this.errorHandler.handleHttpError(e, 'realtime-tracking.load-tracking-map.trip-details.notifications.custom.error'));
  }

  goToMarker([latitude, longitude]: [number, number], loadHandover?: LoadHandover): void {
    const literal = {
      lat: latitude,
      lng: longitude
    };
    this.onGoToMarker.emit(literal);
    if (loadHandover) {
      this.onUpdateLoadHandover.emit(loadHandover);
    }
  }

  getHandoverVehicleForEvent(event: TripEvent): string {
    const targetHandover = this.trip.loadHandovers.find(handover => handover.id === event.referenceId);
    return event.type === TripEventType.HANDOVER_RELEASE ? targetHandover.releasedByVehicle : targetHandover.takenByVehicle;
  }
}
